import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import GaugeChart from "@/components/growth/GaugeChart";
import { Search, TrendingUp, TrendingDown, Plus, Sparkles, FileText, Target, BarChart3, Loader2, Trash2 } from "lucide-react";
import { useTrackedKeywords } from "@/hooks/useGrowthMarketing";

type Tab = "overview" | "keywords" | "content" | "competitors" | "reports";

const COMPETITORS = [
  { name: "ComfortKeepers Alexandria", score: 78, keywords: 142, backlinks: 380 },
  { name: "Right at Home NoVA", score: 71, keywords: 118, backlinks: 295 },
  { name: "Visiting Angels Arlington", score: 84, keywords: 167, backlinks: 512 },
  { name: "Home Instead Fairfax", score: 69, keywords: 96, backlinks: 224 },
];

export default function SeoManager() {
  const [tab, setTab] = useState<Tab>("overview");
  const { items: keywords, add, remove, loading } = useTrackedKeywords();
  const [newKw, setNewKw] = useState("");
  const [generating, setGenerating] = useState(false);
  const [contentTopic, setContentTopic] = useState("");

  const addKeyword = async () => {
    if (!newKw.trim()) return;
    try {
      await add(newKw.trim());
      toast.success(`Tracking "${newKw.trim()}"`);
      setNewKw("");
    } catch (e: any) {
      toast.error(e.message ?? "Failed to add keyword");
    }
  };

  const removeKeyword = async (id: string) => {
    try {
      await remove(id);
      toast.success("Keyword removed");
    } catch (e: any) {
      toast.error(e.message ?? "Failed");
    }
  };

  const generateContent = () => {
    if (!contentTopic.trim()) {
      toast.error("Enter a topic first");
      return;
    }
    setGenerating(true);
    setTimeout(() => {
      setGenerating(false);
      toast.success("Content brief generated");
    }, 2400);
  };

  const tabs: { key: Tab; label: string; icon: any }[] = [
    { key: "overview", label: "Overview", icon: BarChart3 },
    { key: "keywords", label: "Keywords", icon: Search },
    { key: "content", label: "Content", icon: FileText },
    { key: "competitors", label: "Competitors", icon: Target },
    { key: "reports", label: "Reports", icon: TrendingUp },
  ];

  return (
    <div className="space-y-6">
      <div className="flex gap-1 border-b overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              tab === t.key ? "border-gold text-navy" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <t.icon className="h-4 w-4" />
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader><CardTitle className="text-base">SEO Health Score</CardTitle></CardHeader>
            <CardContent className="flex justify-center">
              <GaugeChart value={72} label="Good" sublabel="↑ 8 points this month" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Domain Authority</CardTitle></CardHeader>
            <CardContent className="flex justify-center">
              <GaugeChart value={48} label="Building" sublabel="↑ 3 this quarter" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Page Speed</CardTitle></CardHeader>
            <CardContent className="flex justify-center">
              <GaugeChart value={86} label="Excellent" sublabel="LCP 1.8s" />
            </CardContent>
          </Card>
          <Card className="md:col-span-3">
            <CardHeader><CardTitle className="text-base">Top Movers (last 30 days)</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {keywords.length === 0 && <div className="text-sm text-muted-foreground">No keywords tracked yet. Add some on the Keywords tab.</div>}
                {keywords.slice(0, 4).map((k) => {
                  const delta = (k.previous_rank ?? 0) - (k.current_rank ?? 0);
                  return (
                    <div key={k.id} className="flex items-center justify-between py-2 border-b last:border-0">
                      <span className="text-sm">{k.keyword}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-muted-foreground">#{k.current_rank ?? "—"}</span>
                        <Badge className={delta > 0 ? "bg-success/10 text-success" : delta < 0 ? "bg-destructive/10 text-destructive" : "bg-muted"}>
                          {delta > 0 ? <TrendingUp className="h-3 w-3 mr-1" /> : delta < 0 ? <TrendingDown className="h-3 w-3 mr-1" /> : null}
                          {delta > 0 ? `+${delta}` : delta}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {tab === "keywords" && (
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <CardTitle className="text-base">Tracked Keywords ({keywords.length})</CardTitle>
              <div className="flex gap-2">
                <Input placeholder="Add keyword..." value={newKw} onChange={(e) => setNewKw(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addKeyword()} className="w-56" />
                <Button onClick={addKeyword} className="bg-gold text-gold-foreground hover:bg-gold/90"><Plus className="h-4 w-4" /></Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b text-muted-foreground">
                    <th className="py-2">Keyword</th>
                    <th className="py-2">Rank</th>
                    <th className="py-2">Δ</th>
                    <th className="py-2">Volume</th>
                    <th className="py-2">Difficulty</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">Loading…</td></tr>}
                  {!loading && keywords.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">No keywords yet.</td></tr>}
                  {keywords.map((k) => {
                    const delta = (k.previous_rank ?? 0) - (k.current_rank ?? 0);
                    return (
                      <tr key={k.id} className="border-b last:border-0">
                        <td className="py-2.5">{k.keyword}</td>
                        <td className="py-2.5 font-medium">#{k.current_rank ?? "—"}</td>
                        <td className="py-2.5">
                          <span className={delta > 0 ? "text-success" : delta < 0 ? "text-destructive" : "text-muted-foreground"}>
                            {delta > 0 ? `+${delta}` : delta}
                          </span>
                        </td>
                        <td className="py-2.5">{(k.search_volume ?? 0).toLocaleString()}</td>
                        <td className="py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-1.5 rounded-full bg-muted overflow-hidden">
                              <div className="h-full bg-gold" style={{ width: `${k.difficulty ?? 0}%` }} />
                            </div>
                            <span className="text-xs text-muted-foreground">{k.difficulty ?? 0}</span>
                          </div>
                        </td>
                        <td className="py-2.5">
                          <Button variant="ghost" size="sm" onClick={() => removeKeyword(k.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {tab === "content" && (
        <Card>
          <CardHeader><CardTitle className="text-base">AI Content Brief Generator</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input placeholder="Topic, e.g. 'choosing a memory care provider'" value={contentTopic} onChange={(e) => setContentTopic(e.target.value)} />
              <Button onClick={generateContent} disabled={generating} className="bg-gold text-gold-foreground hover:bg-gold/90">
                {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Generate
              </Button>
            </div>
            {generating && (
              <div className="space-y-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Analyzing search intent…</div>
                <div className="flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Identifying related questions…</div>
                <div className="flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Drafting outline…</div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {["Top 10 Signs Your Loved One Needs Help", "Medicare vs Medicaid: Home Care Coverage", "Hiring a Caregiver: A Family's Guide"].map((title) => (
                <div key={title} className="p-4 border rounded-lg hover:border-gold cursor-pointer">
                  <div className="font-medium text-sm">{title}</div>
                  <div className="text-xs text-muted-foreground mt-1">Est. 1,200 words • 3 keywords</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {tab === "competitors" && (
        <Card>
          <CardHeader><CardTitle className="text-base">Competitor Analysis</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {COMPETITORS.map((c) => (
                <div key={c.name} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg gap-3">
                  <div>
                    <div className="font-semibold">{c.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{c.keywords} keywords • {c.backlinks} backlinks</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-2xl font-bold text-navy">{c.score}</div>
                      <div className="text-xs text-muted-foreground">Domain score</div>
                    </div>
                    <Button variant="outline" size="sm">Compare</Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {tab === "reports" && (
        <Card>
          <CardHeader><CardTitle className="text-base">Monthly Reports</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {["April 2025", "March 2025", "February 2025"].map((m) => (
                <div key={m} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-gold" />
                    <div>
                      <div className="font-medium text-sm">SEO Performance — {m}</div>
                      <div className="text-xs text-muted-foreground">12 pages • 8 ranking improvements</div>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => { toast.loading("Preparing PDF…"); setTimeout(() => toast.success("Downloaded"), 1500); }}>Download</Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
